import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Select } from 'antd'
import Form from '$components/Form'
import PropTypes from 'prop-types'
import {
  fetchAvailableColorsWithOamId,
  fetchOperatorAdditiveMaterialsByDwhIds,
} from '$api/evoAPIs'
import { useEffect } from 'react'

const MaterialCell = ({ record, form, onMaterialChange }) => {
  const { t } = useTranslation()

  const mappedDwhIds = record.mapped_dwh_material_ids || []

  const { data: additiveMaterialOptions, isLoading: materialsAreLoading } =
    useQuery({
      queryKey: ['additiveMaterialsByDwhIds', mappedDwhIds],
      queryFn: fetchOperatorAdditiveMaterialsByDwhIds,
      enabled: mappedDwhIds.length > 0,
      select: (data) =>
        (data || []).map((material) => ({
          label: material.material_name,
          value: material.id,
          ...material,
        })),
    })

  const singleAvailableOption =
    additiveMaterialOptions?.length === 1
      ? additiveMaterialOptions[0].value
      : undefined

  const selectedMaterialId = Form.useWatch(
    ['additive_materials', record.id],
    form,
  )

  const { data: availableColorsData, isLoading: isAvailableColorsLoading } =
    useQuery({
      queryKey: ['availableColors', selectedMaterialId],
      queryFn: () => fetchAvailableColorsWithOamId(selectedMaterialId),
      enabled: !!selectedMaterialId,
    })

  useEffect(() => {
    if (
      singleAvailableOption &&
      !form.getFieldValue(['additive_materials', record.id])
    ) {
      form.setFieldsValue({
        additive_materials: { [record.id]: singleAvailableOption },
      })
    }
  }, [singleAvailableOption, form, record.id])

  return (
    <>
      <Form.Item
        name={['additive_materials', record.id]}
        rules={[
          {
            required: true,
            message: t('service.order_part.material_required'),
          },
        ]}
        style={{ marginBottom: '0' }}
      >
        <Select
          style={{ width: '100%' }}
          size="small"
          placeholder={t('parts_master_data.select_material_placeholder')}
          loading={materialsAreLoading}
          optionLabelProp="label"
          onChange={(_value) => {
            form.setFieldsValue({
              selected_post_processing_methods: {
                [record.id]: [],
              },
              colors: { [record.id]: undefined },
            })
            if (onMaterialChange) onMaterialChange(_value)
          }}
        >
          {(additiveMaterialOptions || []).map((material) => (
            <Select.Option
              key={material.id}
              value={material.id}
              label={material.material_name}
            >
              <div>{material.material_name}</div>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {!isAvailableColorsLoading && availableColorsData?.length > 0 && (
        <Form.Item
          name={['colors', record.id]}
          label={t('color_label')}
          rules={[{ required: true, message: t('required') }]}
          style={{ marginBottom: '0', marginTop: '8px' }}
        >
          <Select
            placeholder={t('service.order_part.select_color')}
            options={availableColorsData?.map((c) => ({
              label: t(`my_materials.colors.${c.toLowerCase()}`, c),
              value: c,
            }))}
            size="small"
          />
        </Form.Item>
      )}
    </>
  )
}

MaterialCell.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
}

export default MaterialCell
